import {
    type FragmentType,
    getFragmentData,
    graphql
} from '~/resources/graphql'
import { Decimal } from '~/composables/support/decimal'
import { useLayoutMode } from '~/composables/use-layout-mode'

type GaEventItem = {
    item_id: string
    item_name: string
    coupon: string
    discount: number
    item_variant: string
    location_id: string
    price: number
    quantity: number
    plan_type: string
    plan_name: string
    enrollment_type: string | undefined
}

type MetaEventItem = {
    id: string
    quantity: number
    item_name: string
    coupon: string
    discount: number
    item_variant: string
    price: number
}

function emitGaAndDataLayerAddToCart(input: {
    currencyFormat: string
    items: GaEventItem[]
    shouldTrackGtag: boolean
    shouldTrackDataLayer: boolean
}) {
    if (input.items.length === 0) {
        return
    }

    const eventData = {
        // Sum of (price * quantity) for all items.
        value: input.items.reduce((sum, item) => {
            return sum + item.price * item.quantity
        }, 0),
        currency: input.currencyFormat,
        items: input.items
    }

    if (input.shouldTrackGtag) {
        try {
            // @ts-ignore : ignore gtag not defined
            gtag('event', 'add_to_cart', eventData)
        } catch {
            // no-op
        }
    }

    if (input.shouldTrackDataLayer) {
        try {
            // @ts-ignore : Ignore dataLayer object not defined
            window.dataLayer.push({
                event: 'add_to_cart',
                ...eventData
            })
        } catch {
            // no-op
        }
    }
}

function emitMetaAddToCart(input: {
    currencyFormat: string
    items: MetaEventItem[]
}) {
    if (input.items.length === 0) {
        return
    }

    let hasFbq = false
    try {
        // @ts-ignore
        hasFbq = !!window.fbq
    } catch {
        hasFbq = false
    }

    if (!hasFbq) {
        return
    }

    try {
        // @ts-ignore
        fbq('track', 'AddToCart', {
            // Sum of (price * quantity) for all items.
            value: input.items.reduce((sum, content) => {
                return sum + content.price * content.quantity
            }, 0),
            currency: input.currencyFormat,
            num_items: input.items.reduce((sum, content) => {
                return sum + content.quantity
            }, 0),
            content_ids: input.items.map(content => content.id),
            contents: input.items
        })
    } catch {
        // no-op
    }
}

export const AnalyticsAddToCartResultFragment = graphql(`
    fragment AnalyticsAddToCartResultFragment on PlanContract {
        id
        planType
        publicName
        facility {
            shortName
            organization {
                googleAnalyticsTag
                googleAnalyticsTagEcommerceEvents
                googleAnalyticsTagKioskEcommerceEvents
                dataLayerEcommerceEvents
                dataLayerKioskEcommerceEvents
                metaPixel
                metaPixelEcommerceEvents
                metaPixelKioskEcommerceEvents
                currencyFormat
            }
        }
        previewPricing {
            subtotal
            items {
                __typename
                ... on CartProductItem {
                    quantity
                    unitPrice
                    originalUnitPrice
                    promotionCode
                    productVariant {
                        analyticsIdentifier
                        variantDisplay
                        product {
                            description
                        }
                    }
                    enrollment {
                        enrollmentType {
                            singular
                        }
                    }
                }
            }
        }
    }
`)

export function useAddToCartAnalytics() {
    const { isKiosk } = useLayoutMode()

    function emitAddToCartEvent(
        addToCartResult: FragmentType<typeof AnalyticsAddToCartResultFragment>
    ) {
        if (isKiosk.value) {
            return
        }

        const addToCartResultData = getFragmentData(
            AnalyticsAddToCartResultFragment,
            addToCartResult
        )

        const shouldTrackGtag =
            addToCartResultData.facility.organization.googleAnalyticsTag !==
                '' &&
            addToCartResultData.facility.organization
                .googleAnalyticsTagEcommerceEvents
        const shouldTrackDataLayer =
            addToCartResultData.facility.organization.dataLayerEcommerceEvents
        const shouldTrackMeta =
            addToCartResultData.facility.organization.metaPixel !== '' &&
            addToCartResultData.facility.organization.metaPixelEcommerceEvents

        if (!shouldTrackGtag && !shouldTrackDataLayer && !shouldTrackMeta) {
            return
        }

        const gaItems: GaEventItem[] = []
        const metaItems: MetaEventItem[] = []

        for (const lineItem of addToCartResultData.previewPricing.items) {
            if (lineItem.__typename !== 'CartProductItem') {
                continue
            }

            gaItems.push({
                item_id: lineItem.productVariant.analyticsIdentifier,
                item_name: lineItem.productVariant.product.description,
                coupon: lineItem.promotionCode,
                discount: Number(
                    new Decimal(lineItem.originalUnitPrice)
                        .minus(new Decimal(lineItem.unitPrice))
                        .abs()
                        .toFixed(2)
                ),
                item_variant: lineItem.productVariant.variantDisplay,
                location_id: addToCartResultData.facility.shortName,
                price: Number(lineItem.unitPrice),
                quantity: lineItem.quantity,
                plan_type: addToCartResultData.planType,
                plan_name: addToCartResultData.publicName,
                enrollment_type: lineItem.enrollment?.enrollmentType.singular
            })

            metaItems.push({
                id: lineItem.productVariant.analyticsIdentifier,
                quantity: lineItem.quantity,
                item_name: lineItem.productVariant.product.description,
                coupon: lineItem.promotionCode,
                discount: Number(
                    new Decimal(lineItem.originalUnitPrice)
                        .minus(new Decimal(lineItem.unitPrice))
                        .abs()
                        .toFixed(2)
                ),
                item_variant: lineItem.productVariant.variantDisplay,
                price: Number(lineItem.unitPrice)
            })
        }

        if (shouldTrackGtag || shouldTrackDataLayer) {
            emitGaAndDataLayerAddToCart({
                currencyFormat:
                    addToCartResultData.facility.organization.currencyFormat,
                items: gaItems,
                shouldTrackGtag,
                shouldTrackDataLayer
            })
        }

        if (shouldTrackMeta) {
            emitMetaAddToCart({
                currencyFormat:
                    addToCartResultData.facility.organization.currencyFormat,
                items: metaItems
            })
        }
    }

    return {
        emitAddToCartEvent
    }
}
